import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import RichTextEditor from '../../components/content/RichTextEditor';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { forumService } from '../../services/forumService';
import { fileUploadService } from '../../services/fileUploadService';
import { sanitizeContent } from '../../utils/contentSanitizer';
import type { UploadResult } from '../../services/fileUploadService';
import '../../styles/pages/CreatePost.css';

interface CreatePostPageProps {
  mode?: 'create' | 'edit';
  postId?: string;
}

const CreatePostPage: React.FC<CreatePostPageProps> = ({ mode = 'create', postId }) => {
  const { categorySlug, topicId } = useParams<{ categorySlug: string; topicId?: string }>();
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form state
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [uploadedAttachments, setUploadedAttachments] = useState<UploadResult[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]); // Track which files are uploading
  const [isPoll, setIsPoll] = useState(false);
  const [pollOptions, setPollOptions] = useState<string[]>(['', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current tag input
  const [currentTag, setCurrentTag] = useState('');

  // Redirect to topic creation page if no topicId (creating new topic)
  React.useEffect(() => {
    if (!topicId && mode === 'create') {
      navigate(`/forums/category/${categorySlug}/create-topic`);
      return;
    }
  }, [topicId, mode, navigate, categorySlug]);

  // Don't render if redirecting
  if (!topicId && mode === 'create') {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setError(t('forums.createPost.errors.loginRequired'));
      return;
    }

    if (!title.trim() || !content.trim()) {
      setError(t('forums.createPost.errors.requiredFields'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Sanitize the content before saving
      const sanitizedContent = sanitizeContent(content);

      if (mode === 'create') {
        if (topicId) {
          // Creating a reply to an existing topic
          const attachmentData = uploadedAttachments.map(upload => ({
            id: upload.path,
            filename: upload.name,
            size: upload.size,
            mime_type: upload.type,
            download_url: upload.url
          }));

          await forumService.createPost({
            topic_id: topicId,
            content: sanitizedContent,
            attachments: attachmentData
          });
          navigate(`/forums/${categorySlug}/topics/${topicId}`);
        } else {
          // Creating a new topic
          const newTopic = await forumService.createTopicByCategorySlug(categorySlug!, {
            title: title.trim(),
            description: sanitizedContent,
            tags,
          });

          // If there are attachments (uploaded or pending), handle them
          let finalAttachments: any[] = [];

          // Add already uploaded attachments
          if (uploadedAttachments.length > 0) {
            finalAttachments = uploadedAttachments.map(upload => ({
              id: upload.path,
              filename: upload.name,
              size: upload.size,
              mime_type: upload.type,
              download_url: upload.url
            }));
          }

          // Upload any pending attachments now that we have a topic ID
          if (attachments.length > 0) {
            for (const file of attachments) {
              try {
                const uploadResult = await fileUploadService.uploadForumAttachment(file, newTopic.id);
                finalAttachments.push({
                  id: uploadResult.path,
                  filename: uploadResult.name,
                  size: uploadResult.size,
                  mime_type: uploadResult.type,
                  download_url: uploadResult.url
                });
              } catch (error) {
                console.error('Failed to upload attachment:', error);
                // Continue with other attachments even if one fails
              }
            }
          }

          // Create the first post with all attachments (if any)
          if (finalAttachments.length > 0) {
            await forumService.createPost({
              topic_id: newTopic.id,
              content: content.trim(),
              attachments: finalAttachments
            });
          }

          navigate(`/forums/${categorySlug}/topics/${newTopic.id}`);
        }
      } else {
        // Editing existing post
        const attachmentData = uploadedAttachments.map(upload => ({
          id: upload.path,
          filename: upload.name,
          size: upload.size,
          mime_type: upload.type,
          download_url: upload.url
        }));

        await forumService.updatePost(postId!, {
          content: content.trim(),
          attachments: attachmentData
        });
        navigate(`/forums/${categorySlug}/topics/${topicId}`);
      }
    } catch (error) {
      console.error('Error saving post:', error);
      setError(t('forums.createPost.errors.saveFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTag = () => {
    const trimmedTag = currentTag.trim();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 5) {
      setTags([...tags, trimmedTag]);
      setCurrentTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setError(null);

    // Validate files first
    const validFiles: File[] = [];
    for (const file of files) {
      const validation = fileUploadService.validateFile(file);
      if (!validation.isValid) {
        setError(validation.error || t('forums.createPost.errors.validationFailed'));
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) return;

    // Check total attachment limit
    if (attachments.length + uploadedAttachments.length + validFiles.length > 5) {
      setError(t('forums.createPost.errors.maxAttachments'));
      return;
    }

    // Add files to pending uploads
    setAttachments(prev => [...prev, ...validFiles]);

    // Upload files immediately for existing topics, or keep them pending for new topics
    for (const file of validFiles) {
      setUploadingFiles(prev => [...prev, file.name]);

      try {
        if (topicId) {
          // We're replying to an existing topic - upload immediately
          const uploadResult = await fileUploadService.uploadForumAttachment(file, topicId);

          setUploadedAttachments(prev => [...prev, uploadResult]);

          // Remove from pending uploads
          setAttachments(prev => prev.filter(f => f.name !== file.name));
        } else {
          // We're creating a new topic - files will be uploaded after topic creation
          // Just keep the file in the attachments array for now
          console.log(`File ${file.name} will be uploaded after topic creation`);
        }

      } catch (error) {
        console.error('Upload failed:', error);
        setError(`${t('forums.createPost.errors.uploadFailed', { file: file.name })}: ${error instanceof Error ? error.message : 'Unknown error'}`);
        // Remove failed file from pending uploads
        setAttachments(prev => prev.filter(f => f.name !== file.name));
      } finally {
        setUploadingFiles(prev => prev.filter(name => name !== file.name));
      }
    }

    // Clear the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number, isUploaded = false) => {
    if (isUploaded) {
      setUploadedAttachments(uploadedAttachments.filter((_, i) => i !== index));
    } else {
      setAttachments(attachments.filter((_, i) => i !== index));
    }
  };

  const addPollOption = () => {
    if (pollOptions.length < 10) {
      setPollOptions([...pollOptions, '']);
    }
  };

  const removePollOption = (index: number) => {
    if (pollOptions.length > 2) {
      setPollOptions(pollOptions.filter((_, i) => i !== index));
    }
  };

  const updatePollOption = (index: number, value: string) => {
    const newOptions = [...pollOptions];
    newOptions[index] = value;
    setPollOptions(newOptions);
  };

  const pageTitle = mode === 'create'
    ? (topicId ? t('forums.createPost.title.reply') : t('forums.createPost.title.create'))
    : t('forums.createPost.title.edit');

  return (
    <BasePageLayout>
      <div className="create-post-page">
        <div className="create-post-container">
          <form onSubmit={handleSubmit} className="create-post-form">
            {/* Header */}
            <div className="form-header">
              <h1 className="page-title">
                <i className={topicId ? "fas fa-reply" : "fas fa-plus-circle"}></i>
                {pageTitle}
              </h1>
              <div className="form-actions">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="btn btn-secondary"
                >
                  {t('forums.createPost.form.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !title.trim() || !content.trim()}
                  className="btn btn-primary"
                >
                  {isLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {mode === 'create' ? (
                        attachments.length > 0 && !topicId ? t('forums.createPost.form.submittingCreateUpload') : t('forums.createPost.form.submittingCreate')
                      ) : t('forums.createPost.form.submittingSave')}
                    </>
                  ) : (
                    <>
                      <i className={mode === 'create' ? "fas fa-plus" : "fas fa-save"}></i>
                      {mode === 'create' ? (
                        attachments.length > 0 && !topicId ? t('forums.createPost.form.submitCreateUpload') : t('forums.createPost.form.submitCreate')
                      ) : t('forums.createPost.form.submitSave')}
                    </>
                  )}
                </button>
              </div>
            </div>

            {error && (
              <div className="alert alert-error">
                <i className="fas fa-exclamation-triangle"></i>
                {error}
              </div>
            )}

            {/* Title (only for new topics) */}
            {!topicId && (
              <div className="form-group">
                <label htmlFor="title" className="form-label">
                  {t('forums.createPost.form.titleLabel')} <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={t('forums.createPost.form.titlePlaceholder')}
                  className="form-input"
                  maxLength={200}
                  required
                />
                <div className="char-count">{title.length}/200</div>
              </div>
            )}

            {/* Content */}
            <div className="form-group">
              <label className="form-label">
                {t('forums.createPost.form.contentLabel')} <span className="required">*</span>
              </label>
              <RichTextEditor
                content={content}
                onChange={setContent}
                placeholder={topicId ? t('forums.createPost.form.contentPlaceholderReply') : t('forums.createPost.form.contentPlaceholderPost')}
                className="content-editor"
                topicId={topicId}
              />
              <div className="char-count">{content.replace(/<[^>]*>/g, '').length} characters</div>
            </div>

            {/* Tags (only for new topics) */}
            {!topicId && (
              <div className="form-group">
                <label className="form-label">{t('forums.createPost.form.tagsLabel')}</label>
                <div className="tags-input-container">
                  <div className="tags-display">
                    {tags.map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag)}
                          className="tag-remove"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="tag-input-row">
                    <input
                      type="text"
                      value={currentTag}
                      onChange={(e) => setCurrentTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      placeholder={t('forums.createPost.form.tagsPlaceholder')}
                      className="tag-input"
                      maxLength={30}
                      disabled={tags.length >= 5}
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      disabled={!currentTag.trim() || tags.length >= 5}
                      className="btn btn-outline"
                    >
                      {t('forums.createPost.form.submitCreate')}
                    </button>
                  </div>
                  <div className="help-text">{t('forums.createPost.form.tagsHelp')}</div>
                </div>
              </div>
            )}

            {/* Poll (only for new topics) */}
            {!topicId && (
              <div className="form-group">
                <div className="poll-header">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isPoll}
                      onChange={(e) => setIsPoll(e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    {t('forums.createPost.form.pollInclude')}
                  </label>
                </div>

                {isPoll && (
                  <div className="poll-options">
                    <div className="poll-options-header">
                      <span>{t('forums.createPost.form.pollOptions')}</span>
                      <button
                        type="button"
                        onClick={addPollOption}
                        disabled={pollOptions.length >= 10}
                        className="btn btn-sm btn-outline"
                      >
                        <i className="fas fa-plus"></i>
                        {t('forums.createPost.form.addOption')}
                      </button>
                    </div>
                    {pollOptions.map((option, index) => (
                      <div key={index} className="poll-option-input">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updatePollOption(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                          className="form-input"
                          maxLength={100}
                        />
                        {pollOptions.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removePollOption(index)}
                            className="btn btn-sm btn-danger"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Attachments */}
            <div className="form-group">
              <label className="form-label">{t('forums.createPost.form.attachmentsLabel')}</label>
              <div className="attachments-section">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  multiple
                  accept="image/*,.pdf,.txt"
                  className="file-input-hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={attachments.length + uploadedAttachments.length >= 5}
                  className="btn btn-outline file-upload-btn"
                >
                  <i className="fas fa-paperclip"></i>
                  {t('forums.createPost.form.attachFiles')} ({attachments.length + uploadedAttachments.length}/5)
                </button>
                <div className="help-text">
                  {t('forums.createPost.form.attachmentsHelp')}
                </div>

                {/* Show uploaded attachments */}
                {uploadedAttachments.length > 0 && (
                  <div className="attachments-list">
                    <h4>{t('forums.createPost.form.uploadedFiles')}</h4>
                    {uploadedAttachments.map((upload, index) => (
                      <div key={index} className="attachment-item uploaded">
                        <div className="attachment-info">
                          <i className={`fas ${upload.type.startsWith('image/') ? 'fa-image' :
                              upload.type === 'application/pdf' ? 'fa-file-pdf' :
                                'fa-file-text'
                            }`}></i>
                          <span className="attachment-name">{upload.name}</span>
                          <span className="attachment-size">
                            ({(upload.size / 1024 / 1024).toFixed(2)}MB)
                          </span>
                          <span className="upload-status">✓ {t('forums.createPost.form.statusUploaded')}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index, true)}
                          className="btn btn-small btn-danger"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Show pending uploads */}
                {attachments.length > 0 && (
                  <div className="attachments-list">
                    <h4>{t('forums.createPost.form.uploadingFiles')}</h4>
                    {attachments.map((file, index) => (
                      <div key={index} className="attachment-item pending">
                        <div className="attachment-info">
                          <i className={`fas ${file.type.startsWith('image/') ? 'fa-image' :
                              file.type === 'application/pdf' ? 'fa-file-pdf' :
                                'fa-file-text'
                            }`}></i>
                          <span className="attachment-name">{file.name}</span>
                          <span className="attachment-size">
                            ({(file.size / 1024 / 1024).toFixed(2)}MB)
                          </span>
                          {uploadingFiles.includes(file.name) ? (
                            <span className="upload-status">📤 {t('forums.createPost.form.statusUploading')}</span>
                          ) : (
                            <span className="upload-status">⏳ {t('forums.createPost.form.statusPending')}</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveAttachment(index, false)}
                          className="btn btn-small btn-danger"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Guidelines */}
            <div className="guidelines">
              <h4>
                <i className="fas fa-info-circle"></i>
                {t('forums.createPost.guidelines.title')}
              </h4>
              <ul>
                <li>{t('forums.createPost.guidelines.item1')}</li>
                <li>{t('forums.createPost.guidelines.item2')}</li>
                <li>{t('forums.createPost.guidelines.item3')}</li>
                <li>{t('forums.createPost.guidelines.item4')}</li>
                <li>{t('forums.createPost.guidelines.item5')}</li>
              </ul>
            </div>
          </form>
        </div>
      </div>
    </BasePageLayout>
  );
};

export default CreatePostPage;
